import { useCallback, useEffect, useState } from 'react';
import { MdPushPin, MdClose, MdRefresh, MdHistory, MdVisibility } from 'react-icons/md';
import izenuAPI from '../../../services/izenuAPI';

const STRATEGY = {
    lancamentos: 'As faixas aprovadas mais recentes, no máximo 2 por artista.',
    genero: 'O género com mais ouvintes na semana, sem repetir os das últimas 3 semanas. Um terço das faixas são as menos mostradas desse género.',
    top: 'As mais ouvidas, contadas por pessoas e não por reproduções.',
};
const FREQUENCY = { daily: 'todos os dias', weekly: 'às segundas' };
const REASON = { auto: 'automático', manual_run: 'gerado no painel', restore: 'reposição' };

/**
 * Automatic track lists for 666 / 667 / 668. Only the songs inside change:
 * the playlists, their verification and their place on the home stay as they are.
 */
export default function WeeklyAutoPanel({ onChanged }) {
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);
    const [preview, setPreview] = useState({});
    const [openTracks, setOpenTracks] = useState({});
    const [pinInput, setPinInput] = useState({});

    const load = useCallback(async () => {
        try {
            const { data: r } = await izenuAPI.get('/admin/playlists/weeklyAuto.php');
            if (r.response) { setItems(r.data); setError(null); }
        } catch (err) {
            setError(err?.response?.data?.message || err.message);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const post = async (key, body, after) => {
        setBusy(key);
        try {
            const { data: r } = await izenuAPI.post('/admin/playlists/weeklyAuto.php', body);
            if (r.data) setItems(r.data);
            if (after) after(r);
            if (onChanged && ['run', 'restore'].includes(body.action)) onChanged();
        } catch (err) {
            alert(err?.response?.data?.message || err.message);
        }
        setBusy(null);
    };

    const setMode = (it, mode) => {
        if (mode === 'auto' && !window.confirm(
            `Passar «${it.title}» a automática?\n\nAs músicas passam a ser trocadas pelo sistema (${FREQUENCY[it.frequency]}, às 04h). ` +
            'A playlist e o lugar dela na home não mudam, e a lista actual fica guardada no histórico para poderes repor.'
        )) return;
        post(`mode-${it.playlist_id}`, { action: 'mode', playlist_id: it.playlist_id, mode });
    };

    const savePins = (it, ids) => post(`pin-${it.playlist_id}`, { action: 'pinned', playlist_id: it.playlist_id, music_ids: ids });

    const box = { backgroundColor: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e0e0e0' };
    const btn = (color, filled) => ({
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, fontSize: 13,
        fontWeight: 600, cursor: 'pointer', border: `1px solid ${color}`,
        backgroundColor: filled ? color : '#fff', color: filled ? '#fff' : color,
    });
    const small = { fontSize: 12, color: '#888' };

    if (error) {
        return <p style={{ ...small, marginBottom: 24 }}>Conteúdo automático indisponível: {error}</p>;
    }
    if (!items.length) return null;

    return (
        <div style={{ marginBottom: 32 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 18, color: '#222' }}>Conteúdo automático</h3>
            <p style={{ margin: '0 0 16px', maxWidth: 720, fontSize: 14, lineHeight: 1.5, color: '#555', textAlign: 'left' }}>
                Em <strong>Automática</strong>, o sistema troca só as músicas destas playlists. A playlist, a verificação e o
                lugar na home nunca mudam. Antes de cada troca a lista anterior fica no histórico. Se não houver músicas
                suficientes, fica a lista que já lá está.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
                {items.map(it => {
                    const pid = it.playlist_id;
                    const pinnedIds = it.pinned.map(t => t.music_id);
                    const pv = preview[pid];
                    return (
                        <div key={pid} style={box}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                                {it.image_url && <img src={it.image_url} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, color: '#222' }}>{it.title}</div>
                                    <div style={small}>id {pid}{it.subtitle ? ` · ${it.subtitle}` : ''} · {it.tracks.length} faixas</div>
                                </div>
                            </div>

                            {/* Mode */}
                            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #ccc', marginBottom: 8 }}>
                                {[['manual', 'Manual'], ['auto', 'Automática']].map(([m, label]) => (
                                    <button
                                        key={m}
                                        disabled={busy !== null || it.mode === m}
                                        onClick={() => setMode(it, m)}
                                        style={{
                                            flex: 1, padding: '8px 0', border: 'none', fontSize: 13, fontWeight: 600,
                                            cursor: it.mode === m ? 'default' : 'pointer',
                                            backgroundColor: it.mode === m ? (m === 'auto' ? '#4CAF50' : '#555') : '#fff',
                                            color: it.mode === m ? '#fff' : '#555',
                                        }}
                                    >{label}</button>
                                ))}
                            </div>
                            <p style={{ ...small, margin: '0 0 10px', lineHeight: 1.4, textAlign: 'left' }}>
                                {STRATEGY[it.strategy]} Muda {FREQUENCY[it.frequency]}.
                            </p>
                            {it.last_run_at && (
                                <p style={{ ...small, margin: '0 0 10px', textAlign: 'left' }}>
                                    Última execução: {it.last_run_at} UTC — {it.last_result}
                                </p>
                            )}

                            {/* Pinned */}
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#333', margin: '12px 0 6px', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <MdPushPin size={14} /> Fixadas no topo
                            </div>
                            {it.pinned.length === 0 && <p style={{ ...small, margin: '0 0 6px', textAlign: 'left' }}>Nenhuma. Fixa a partir da lista actual ou pelo id da música.</p>}
                            {it.pinned.map(t => (
                                <div key={t.music_id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '2px 0' }}>
                                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: t.playable ? '#333' : '#c62828' }}>
                                        {t.title} — {t.singer}{!t.playable && ' (não pode tocar, ignorada)'}
                                    </span>
                                    <button onClick={() => savePins(it, pinnedIds.filter(id => id !== t.music_id))} disabled={busy !== null}
                                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#999' }} title="Desafixar">
                                        <MdClose size={16} />
                                    </button>
                                </div>
                            ))}
                            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                <input
                                    type="number"
                                    placeholder="id da música"
                                    value={pinInput[pid] || ''}
                                    onChange={e => setPinInput({ ...pinInput, [pid]: e.target.value })}
                                    style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                                />
                                <button
                                    style={btn('#1976d2')}
                                    disabled={busy !== null || !pinInput[pid]}
                                    onClick={() => {
                                        savePins(it, [...pinnedIds, Number(pinInput[pid])]);
                                        setPinInput({ ...pinInput, [pid]: '' });
                                    }}
                                >Fixar</button>
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                                <button style={btn('#1976d2')} disabled={busy !== null}
                                    onClick={() => post(`pv-${pid}`, { action: 'preview', playlist_id: pid }, r => setPreview({ ...preview, [pid]: r.preview }))}>
                                    <MdVisibility size={15} /> {busy === `pv-${pid}` ? 'A calcular…' : 'Pré-visualizar'}
                                </button>
                                <button style={btn('#4CAF50', true)} disabled={busy !== null}
                                    onClick={() => {
                                        if (!window.confirm(`Trocar já as músicas de «${it.title}»? A lista actual fica no histórico.`)) return;
                                        post(`run-${pid}`, { action: 'run', playlist_id: pid }, r => {
                                            setPreview({ ...preview, [pid]: null });
                                            alert(r.result?.unchanged ? 'Sem alterações: a lista já é esta.' : `Feito: ${r.result?.count} faixas.`);
                                        });
                                    }}>
                                    <MdRefresh size={15} /> {busy === `run-${pid}` ? 'A gerar…' : 'Gerar agora'}
                                </button>
                                <button style={btn('#555')} onClick={() => setOpenTracks({ ...openTracks, [pid]: !openTracks[pid] })}>
                                    {openTracks[pid] ? 'Esconder lista actual' : 'Ver lista actual'}
                                </button>
                            </div>

                            {pv && (
                                <div style={{ marginTop: 12, padding: 10, borderRadius: 8, backgroundColor: '#f1f8e9', border: '1px solid #c5e1a5' }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#33691e' }}>
                                        Pré-visualização: {pv.tracks.length} faixas{pv.subtitle ? ` · ${pv.subtitle}` : ''} (nada foi gravado)
                                    </div>
                                    <ol style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#333', maxHeight: 220, overflowY: 'auto', textAlign: 'left' }}>
                                        {pv.tracks.map(t => <li key={t.music_id}>{t.title} — {t.singer}</li>)}
                                    </ol>
                                </div>
                            )}

                            {openTracks[pid] && (
                                <ol style={{ margin: '12px 0 0', paddingLeft: 20, fontSize: 12, color: '#333', maxHeight: 260, overflowY: 'auto', textAlign: 'left' }}>
                                    {it.tracks.map(t => (
                                        <li key={t.music_id} style={{ padding: '2px 0' }}>
                                            <span style={{ color: t.playable ? '#333' : '#999' }}>{t.title} — {t.singer}</span>
                                            {!pinnedIds.includes(t.music_id) && (
                                                <button onClick={() => savePins(it, [...pinnedIds, t.music_id])} disabled={busy !== null}
                                                    style={{ marginLeft: 6, border: 'none', background: 'none', cursor: 'pointer', color: '#1976d2', fontSize: 12 }}>
                                                    fixar
                                                </button>
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            )}

                            {/* History */}
                            {it.history.length > 0 && (
                                <details style={{ marginTop: 12 }}>
                                    <summary style={{ fontSize: 13, fontWeight: 600, color: '#333', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <MdHistory size={15} /> Versões anteriores ({it.history.length})
                                    </summary>
                                    {it.history.map(h => (
                                        <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                                            <span style={{ flex: 1, color: '#555', textAlign: 'left' }}>
                                                {h.created_at} · {h.count} faixas{h.subtitle ? ` · ${h.subtitle}` : ''}
                                                <span style={{ color: '#999' }}> (antes de: {REASON[h.reason] || h.reason})</span>
                                            </span>
                                            <button style={btn('#e65100')} disabled={busy !== null}
                                                onClick={() => {
                                                    if (!window.confirm('Repor esta versão? A playlist passa a Manual, para o sistema não a trocar outra vez.')) return;
                                                    post(`rs-${h.id}`, { action: 'restore', history_id: h.id }, () => alert('Versão reposta. A playlist passou a Manual.'));
                                                }}>Repor</button>
                                        </div>
                                    ))}
                                </details>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
